import { z } from "zod";
import { latitudeSchema, longitudeSchema, uuidSchema } from "./common";
import { ORG_ADDRESS_ALIAS_MAX, ORG_ADDRESS_STREET_MAX } from "./org-addresses";
import { adminCreateShadowOrgRequestSchema } from "./admin";
import { SUPPORTED_PROVINCE_CODES } from "../catalogs";
import {
  employmentTypeSchema,
  postingRequirementsSchema,
  preferredLanguageSchema,
  salaryTypeSchema,
  seasonalRangeSchema,
  venueTypeSchema,
  workerPositionSchema,
} from "./postings";

// ============================================================================
// Admin bulk-import of scraped postings (Facebook group → chốt ca)
//
// One scraped FB post can yield N job rows (e.g. "tuyển kế toán + quản lý" →
// 2 rows). Each row carries: how to resolve the org/branch, the posting
// fields (same shape as createPostingRequest minus addressIds — addresses come
// from the org plan), the contacts to stamp on the org (NEVER in the
// description — content policy strips them), and provenance for dedupe.
// ============================================================================

// VND amount: number or bigint (API coerces to bigint). Mirrors postings.ts.
const salaryVndSchema = z.union([z.number().int().nonnegative(), z.bigint()]);

/**
 * Branch address for the import path. Same fields as createOrgAddressRequest
 * but lat/lng/mapUrl are OPTIONAL — the scraping agent only has the address as
 * text (no Google Maps share link), so the API geocodes the address to fill
 * coordinates + a Maps link. Supply them only if a real Maps link is known.
 */
export const importAddressSchema = z.object({
  alias: z.string().min(1).max(ORG_ADDRESS_ALIAS_MAX),
  address: z.string().min(1).max(ORG_ADDRESS_STREET_MAX),
  // Legacy admin fields — optional now. The 2025 ward is resolved server-side
  // from the geocoded coords (point-in-polygon), so imports don't supply it.
  district: z.string().max(60).optional().default(""),
  city: z.string().max(60).optional().default(""),
  provinceCode: z.enum(SUPPORTED_PROVINCE_CODES).nullable().optional(),
  lat: latitudeSchema.optional(),
  lng: longitudeSchema.optional(),
  mapUrl: z.string().url().nullable().optional(),
});

/**
 * How to attach the row to an organization. Exactly one of three modes:
 *  - `existing`   : org + branch both already exist → reuse orgId + addressId.
 *  - `new_branch` : org exists but this is a new location → create OrgAddress
 *                   on the existing org from `newAddress`.
 *  - `new_org`    : brand-new business → create a shadow org from `newOrg`
 *                   (its address fields become the primary branch).
 */
export const importOrgPlanSchema = z
  .object({
    mode: z.enum(["existing", "new_branch", "new_org"]),
    orgId: uuidSchema.optional(),
    addressId: uuidSchema.optional(),
    newAddress: importAddressSchema.optional(),
    newOrg: adminCreateShadowOrgRequestSchema.optional(),
    // AI's confidence + reason for the org match, surfaced for admin review.
    matchConfidence: z.number().min(0).max(1).optional(),
    matchReason: z.string().max(300).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === "existing" && (!v.orgId || !v.addressId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "mode=existing cần orgId + addressId",
      });
    }
    if (v.mode === "new_branch" && (!v.orgId || !v.newAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "mode=new_branch cần orgId + newAddress",
      });
    }
    if (v.mode === "new_org" && !v.newOrg) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "mode=new_org cần newOrg",
      });
    }
  });

/** Posting fields = createPostingRequest WITHOUT addressIds. */
export const importPostingFieldsSchema = z
  .object({
    position: workerPositionSchema,
    title: z.string().min(1).max(120),
    description: z.string().min(1).max(5000),
    venueType: venueTypeSchema.optional(),
    employmentType: employmentTypeSchema,
    salaryMin: salaryVndSchema,
    salaryMax: salaryVndSchema,
    salaryType: salaryTypeSchema,
    headcount: z.number().int().min(1).max(50),
    benefits: z.array(z.string()).max(20).default([]),
    skillsRequired: z.array(z.string()).max(15).default([]),
    skillsPreferred: z.array(z.string()).max(15).default([]),
    requirements: postingRequirementsSchema.optional(),
    preferredLanguages: z.array(preferredLanguageSchema).max(5).optional(),
    softNotes: z.string().max(500).nullable().optional(),
    seasonalRange: seasonalRangeSchema.nullable().optional(),
  })
  .refine((v) => v.salaryMin <= v.salaryMax, {
    message: "salaryMin must be <= salaryMax",
    path: ["salaryMin"],
  });

/**
 * Contacts extracted from the post. Stamped onto the org (revealed only at
 * COMMIT), never written into title/description.
 */
export const importContactsSchema = z.object({
  name: z.string().max(120).optional(),
  phone: z.string().max(40).optional(),
  zalo: z.string().max(120).optional(),
  facebook: z.string().max(200).optional(),
  email: z.string().max(120).optional(),
});

export const adminImportPostingRowSchema = z.object({
  org: importOrgPlanSchema,
  posting: importPostingFieldsSchema,
  contacts: importContactsSchema.optional(),
  // Source FB permalink. Many jobs can share one permalink (one post → N
  // jobs), so dedupe is by (sourceUrl + position + title), not sourceUrl alone.
  sourceUrl: z.string().max(500).optional(),
  // Original FB post id (fbid) for traceability.
  sourcePostId: z.string().max(64).optional(),
  // AI confidence for the whole row (0..1) — drives review highlighting.
  confidence: z.number().min(0).max(1).optional(),
  // Free-form notes for the admin reviewer (vague address, needs branch, etc.).
  warnings: z.array(z.string().max(300)).max(20).optional(),
});

export const adminImportPostingsRequestSchema = z.object({
  // Postings are created OPEN (the upload-preview screen is the review gate —
  // the admin validates/edits/deselects rows before sending). The system has
  // no DRAFT lifecycle for postings, so we don't create unreachable drafts.
  rows: z.array(adminImportPostingRowSchema).min(1).max(200),
});

export const adminImportPostingResultSchema = z.object({
  index: z.number().int(),
  status: z.enum(["created", "skipped_duplicate", "failed"]),
  postingId: uuidSchema.nullable().optional(),
  orgId: uuidSchema.nullable().optional(),
  addressId: uuidSchema.nullable().optional(),
  orgCreated: z.boolean().optional(),
  addressCreated: z.boolean().optional(),
  error: z.string().nullable().optional(),
});

export const adminImportPostingsResponseSchema = z.object({
  batchId: z.string(),
  total: z.number().int(),
  created: z.number().int(),
  skippedDuplicate: z.number().int(),
  failed: z.number().int(),
  results: z.array(adminImportPostingResultSchema),
});

export type ImportAddress = z.infer<typeof importAddressSchema>;
export type ImportOrgPlan = z.infer<typeof importOrgPlanSchema>;
export type ImportPostingFields = z.infer<typeof importPostingFieldsSchema>;
export type ImportContacts = z.infer<typeof importContactsSchema>;
export type AdminImportPostingRow = z.infer<typeof adminImportPostingRowSchema>;
export type AdminImportPostingsRequest = z.infer<
  typeof adminImportPostingsRequestSchema
>;
export type AdminImportPostingResult = z.infer<
  typeof adminImportPostingResultSchema
>;
export type AdminImportPostingsResponse = z.infer<
  typeof adminImportPostingsResponseSchema
>;
